import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Upload, Trash2, Film, FileVideo, Search, X, LayoutGrid, Image, Play, MoreVertical, Tag, Link, Edit3, ListPlus, Calendar, RefreshCw, Copy, Shield, User, Folder, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import VideoPreviewModal from '../components/VideoPreviewModal';
import EditTagsModal from '../components/EditTagsModal';
import { useAuth } from '../context/AuthContext';
import devLog from '../utils/devLog';
import { useAssets } from '../hooks/useAssets';

// Hard-coded movement categories (same as in EditTagsModal)
const MOVEMENT_CATEGORIES = [
  { id: 'upper-body-push', name: 'Upper Body Push', color: '#e53935', type: 'movement' },
  { id: 'upper-body-pull', name: 'Upper Body Pull', color: '#8e24aa', type: 'movement' },
  { id: 'lower-body', name: 'Lower Body', color: '#1e88e5', type: 'movement' },
  { id: 'core', name: 'Core', color: '#00acc1', type: 'movement' },
  { id: 'cardio', name: 'Cardio', color: '#ff5722', type: 'movement' },
  { id: 'upper-body-warmup', name: 'Upper Body Warm-up', color: '#ff9800', type: 'movement' },
  { id: 'lower-body-warmup', name: 'Lower Body Warm-up', color: '#4caf50', type: 'movement' },
  { id: 'total-body-warmup', name: 'Total Body Warm-up', color: '#9c27b0', type: 'movement' },
  { id: 'movement-warmup', name: 'Movement Warm-up', color: '#607d8b', type: 'movement' },
];


function Assets() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadDetails, setUploadDetails] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [, setUploadQueue] = useState([]);
  const [, setCurrentUploadIndex] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const fileInputRef = useRef(null);
  const userFileInputRef = useRef(null);
  const uploadStartTime = useRef(null);
  const { currentUser, isAdmin } = useAuth();

  // VIEW MODE STATE - Admin/User toggle with localStorage persistence
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('assetsViewMode') || 'admin';
  });

  // SCOPE STATE - Library/My Files toggle (User view only) with localStorage persistence
  // 'library' = shared content (no userId on asset)
  // 'my-files' = tenant-private content (userId matches currentUser)
  const [scope, setScope] = useState(() => {
    return localStorage.getItem('assetsScope') || 'library';
  });
  
  // Equipment profile for filtering in User mode
  const [userEquipmentProfile, setUserEquipmentProfile] = useState({});
  const [equipmentListFull, setEquipmentListFull] = useState([]); // Full equipment list with names

  // Replace Video state
  const [replaceVideoAsset, setReplaceVideoAsset] = useState(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const replaceFileInputRef = useRef(null);
  
  // Toast notification state
  const [toast, setToast] = useState(null);

  // Tag filter state
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagInputValue, setTagInputValue] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [highlightedTagIndex, setHighlightedTagIndex] = useState(0);
  const [equipmentList, setEquipmentList] = useState([]);
  const [bodyGroupList, setBodyGroupList] = useState([]);
  const [searchMode, setSearchMode] = useState('name');
  const tagInputRef = useRef(null);
  const tagContainerRef = useRef(null);

  // Multi-select state
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Mobile detection and toast
  const [isMobile, setIsMobile] = useState(() => {
    return window.innerWidth <= 768 || 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  });
  const [showMobileToast, setShowMobileToast] = useState(false);

  // Video preview modal state
  const [previewAsset, setPreviewAsset] = useState(null);

  // Layout preview modal state (for User mode)
  const [previewLayoutId, setPreviewLayoutId] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // Edit tags modal state
  const [editTagsAsset, setEditTagsAsset] = useState(null);

  // Add to playlist modal state
  const [addToPlaylistAsset, setAddToPlaylistAsset] = useState(null);



  // Persist viewMode to localStorage
  useEffect(() => {
    localStorage.setItem('assetsViewMode', viewMode);
  }, [viewMode]);

  // Persist scope to localStorage
  useEffect(() => {
    localStorage.setItem('assetsScope', scope);
  }, [scope]);

  useEffect(() => {
  if (!isAdmin) setViewMode('user');
}, [isAdmin]);

  useEffect(() => {
    const handleResize = () => {
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(window.innerWidth <= 768 || isTouch);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (showMobileToast) {
      const timer = setTimeout(() => setShowMobileToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showMobileToast]);

  // Auto-hide general toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleLayoutClick = (layoutId) => {
    if (viewMode === 'user') {
      // User mode: show preview modal (both desktop and mobile)
      setPreviewLayoutId(layoutId);
    } else {
      // Admin mode
      if (isMobile) {
        setShowMobileToast(true);
      } else {
        navigate(`/layout/${layoutId}`);
      }
    }
  };

  // Shared asset loading + signing (extracted to src/hooks/useAssets.js)
  const { assets, signedUrlMap, loading } = useAssets({ viewMode, scope });

  // Fetch equipment and body groups for tag autocomplete
  useEffect(() => {
    const fetchTagData = async () => {
      try {
        // Fetch equipment
        const equipmentSnapshot = await getDocs(collection(db, 'equipment'));
        const equipment = equipmentSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'equipment'
        }));
        equipment.sort((a, b) => a.name.localeCompare(b.name));
        setEquipmentList(equipment);
        setEquipmentListFull(equipment); // Store full list for profile matching

        // Fetch body groups from Firebase
        const bodyGroupsSnapshot = await getDocs(collection(db, 'bodyGroups'));
        const bodyGroups = bodyGroupsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'bodygroup'
        }));
        bodyGroups.sort((a, b) => a.name.localeCompare(b.name));
        setBodyGroupList(bodyGroups);
      } catch (error) {
        console.error('Error fetching tag data:', error);
      }
    };
    fetchTagData();
  }, []);

  // Fetch user's equipment profile for filtering
  useEffect(() => {
  const fetchEquipmentProfile = async () => {
    if (!currentUser?.uid) return;
    try {
      const profileDoc = await getDoc(doc(db, 'equipmentProfiles', isAdmin ? 'hit10fitness' : currentUser.uid));
        if (profileDoc.exists()) {
          const rawEquipment = profileDoc.data().equipment || {};
          // Clean up any invalid keys
          const equipment = {};
          for (const [key, value] of Object.entries(rawEquipment)) {
            if (key && key !== 'undefined' && key !== 'null' && /^[a-zA-Z0-9]{20}$/.test(key)) {
              equipment[key] = value;
            }
          }
          setUserEquipmentProfile(equipment);
        }
      } catch (error) {
        console.error('Error fetching equipment profile:', error);
      }
    };
    fetchEquipmentProfile();
  }, [currentUser?.uid]);

  // DEBUG: Log hidden videos when data is ready
  useEffect(() => {
    if (assets.length > 0 && equipmentListFull.length > 0 && Object.keys(userEquipmentProfile).length > 0) {
      const selectedEquipmentIds = Object.keys(userEquipmentProfile);
      const userEquipmentNames = equipmentListFull
        .filter(eq => selectedEquipmentIds.includes(eq.id))
        .map(eq => eq.name);
      
      devLog('=== EQUIPMENT FILTER DEBUG ===');
      devLog('User has', userEquipmentNames.length, 'equipment types selected');
      
      const hiddenVideos = assets.filter(asset => {
        if (asset.type !== 'video') return false;
        const videoTags = asset.equipmentTags || [];
        if (videoTags.length === 0) return false;
        const missingTags = videoTags.filter(tag => !userEquipmentNames.includes(tag));
        return missingTags.length > 0;
      });
      
      if (hiddenVideos.length > 0) {
        devLog('❌ HIDDEN VIDEOS (' + hiddenVideos.length + '):');
        hiddenVideos.forEach(video => {
          const videoTags = video.equipmentTags || [];
          const missingTags = videoTags.filter(tag => !userEquipmentNames.includes(tag));
          devLog('  -', video.name, '→ Missing:', missingTags.join(', '));
        });
      } else {
        devLog('✅ All videos match equipment profile');
      }
    }
  }, [assets, equipmentListFull, userEquipmentProfile]);

  // Handle click outside to close tag suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tagContainerRef.current && !tagContainerRef.current.contains(e.target)) {
        setShowTagSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // All available tags for autocomplete: Equipment + Movement + Body Groups
  const allTags = [...equipmentList, ...MOVEMENT_CATEGORIES, ...bodyGroupList];

  // Filter suggestions based on input
  const filteredTagSuggestions = allTags.filter(tag =>
    !selectedTags.find(t => t.id === tag.id) &&
    (tag.name.toLowerCase().includes(tagInputValue.toLowerCase()) ||
     (tag.abbrev && tag.abbrev.toLowerCase().includes(tagInputValue.toLowerCase())))
  );

  // Get equipment names that user has selected in their profile
  const getUserEquipmentNames = () => {
  const selectedEquipmentIds = Object.keys(userEquipmentProfile);


  return equipmentListFull
    .filter(eq => selectedEquipmentIds.includes(eq.id))
    .map(eq => eq.name);
};

  // Check if a video matches the user's equipment profile
  // Video shows ONLY if user has ALL required equipment
  const videoMatchesEquipmentProfile = (asset) => {
    if (asset.type !== 'video') return true; // Non-videos always show
    
    const videoEquipmentTags = asset.equipmentTags || [];
    if (videoEquipmentTags.length === 0) return true; // Videos with no tags always show
    
    const userEquipmentNames = getUserEquipmentNames();
    
    // ALL equipment tags on the video must be in user's profile
    return videoEquipmentTags.every(tag => userEquipmentNames.includes(tag));
  };

  // Filter assets based on search query, type filter, selected tags, AND equipment profile (in User mode)
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // In User mode: users see their own images in My Files, but never in Library
    // (Library is shared videos only). The scope filter upstream already limits
    // My Files to the user's own docs, so here we only exclude images in Library.
    let matchesType;
    if (viewMode === 'user') {
      if (asset.type === 'image' && scope !== 'my-files') return false;
      if (typeFilter === 'all') matchesType = true;
      else if (typeFilter === 'interval') matchesType = asset.type === 'video' && asset.source !== 'exercise-builder';
      else if (typeFilter === 'exercise') matchesType = asset.source === 'exercise-builder' && asset.pageMode !== 'program';
      else if (typeFilter === 'program') matchesType = asset.source === 'exercise-builder' && asset.pageMode === 'program';
      else matchesType = asset.type === typeFilter;
    } else {
      if (typeFilter === 'all') matchesType = true;
      else if (typeFilter === 'interval') matchesType = asset.type === 'video' && asset.source !== 'exercise-builder';
      else if (typeFilter === 'exercise') matchesType = asset.source === 'exercise-builder' && asset.pageMode !== 'program';
      else if (typeFilter === 'program') matchesType = asset.source === 'exercise-builder' && asset.pageMode === 'program';
      else matchesType = asset.type === typeFilter;
    }
    
    // Check tag filters
    const matchesTags = selectedTags.length === 0 || selectedTags.every(tag => {
      if (tag.type === 'equipment') {
        return (asset.equipmentTags || []).includes(tag.name);
      } else if (tag.type === 'movement') {
        return (asset.movementCategories || []).includes(tag.id);
      } else if (tag.type === 'bodygroup') {
        const allBodyGroups = [
          ...(asset.primaryBodyGroups || []),
          ...(asset.secondaryBodyGroups || []),
          ...(asset.bodyGroups || [])
        ];
        return allBodyGroups.includes(tag.name);
      }
      return false;
    });
    
    // In User mode, also filter by equipment profile
    const matchesEquipment = viewMode === 'admin' || videoMatchesEquipmentProfile(asset);
    
    return matchesSearch && matchesType && matchesTags && matchesEquipment;
  });

  // Tag filter functions
  const addTag = (tag) => {
    setSelectedTags(prev => [...prev, tag]);
    setTagInputValue('');
    setShowTagSuggestions(false);
    setHighlightedTagIndex(0);
    tagInputRef.current?.focus();
  };

  const removeTag = (tagId) => {
    setSelectedTags(prev => prev.filter(t => t.id !== tagId));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Backspace' && tagInputValue === '' && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1].id);
    } else if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (filteredTagSuggestions.length > 0 && showTagSuggestions) {
        addTag(filteredTagSuggestions[highlightedTagIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedTagIndex(prev =>
        prev < filteredTagSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedTagIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Escape') {
      setShowTagSuggestions(false);
    }
  };

  const handleTagInputChange = (e) => {
    const value = e.target.value;
    if (value.endsWith(',')) {
      if (filteredTagSuggestions.length > 0) {
        addTag(filteredTagSuggestions[highlightedTagIndex]);
      }
      return;
    }
    setTagInputValue(value);
    setShowTagSuggestions(true);
    setHighlightedTagIndex(0);
  };

  // Multi-select functions
  const toggleAssetSelection = (assetId, e) => {
    e.stopPropagation();
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedAssets.size === 0) return;
    const count = selectedAssets.size;
    if (!window.confirm(`Are you sure you want to delete ${count} asset${count > 1 ? 's' : ''}? They will be removed from all playlists.`)) {
      return;
    }
    setIsDeleting(true);
    try {
      const playlistsSnapshot = await getDocs(collection(db, 'playlists'));
      for (const assetId of selectedAssets) {
        for (const playlistDoc of playlistsSnapshot.docs) {
          const playlistData = playlistDoc.data();
          const assets = playlistData.assets || [];
          if (assets.includes(assetId)) {
            const updatedAssets = assets.filter(id => id !== assetId);
            await updateDoc(doc(db, 'playlists', playlistDoc.id), { assets: updatedAssets });
          }
        }
        await deleteDoc(doc(db, 'assets', assetId));
      }
      setSelectedAssets(new Set());
    } catch (error) {
      console.error('Error deleting assets:', error);
      alert('Error deleting assets: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'image/jpeg', 'image/png', 'image/gif'];
    const validFiles = fileArray.filter(file => {
      if (!validTypes.includes(file.type)) {
        alert(`Skipping ${file.name}: Invalid file type. Please use MP4, WebM, MOV, JPG, PNG, or GIF.`);
        return false;
      }
      return true;
    });
    if (validFiles.length === 0) return;
    setPendingFiles(validFiles);
    setShowUploadModal(true);
    setUploading(false);
    setUploadProgress(0);
    setUploadDetails(null);
  };

  const startUpload = async () => {
    if (pendingFiles.length === 0) return;
    const queue = pendingFiles.map(file => ({ file, name: file.name, size: file.size, type: file.type, status: 'pending' }));
    setUploadQueue(queue);
    setCurrentUploadIndex(0);
    setUploading(true);
    for (let i = 0; i < queue.length; i++) {
      setCurrentUploadIndex(i);
      await uploadSingleFile(queue[i].file, i, queue.length);
    }
    setUploading(false);
    setUploadQueue([]);
    setUploadDetails(null);
    setUploadProgress(0);
    setShowUploadModal(false);
    setPendingFiles([]);
  };

  const cancelUpload = () => {
    setShowUploadModal(false);
    setPendingFiles([]);
    setUploading(false);
    setUploadDetails(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addMoreFiles = () => { fileInputRef.current?.click(); };

  const removePendingFile = (index) => {
    setPendingFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      if (newFiles.length === 0) setShowUploadModal(false);
      return newFiles;
    });
  };

  const uploadSingleFile = async (file, index, total) => {
    uploadStartTime.current = Date.now();
    setUploadDetails({ fileName: file.name, fileSize: file.size, uploadedBytes: 0, currentIndex: index + 1, totalFiles: total, status: 'uploading' });
    setUploadProgress(0);
    try {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${safeName}`;
      const isVideo = file.type.startsWith('video/');
      const fileType = isVideo ? 'video' : 'image';
      const functions = getFunctions();
      const getUploadUrl = httpsCallable(functions, 'getUploadUrl');
      const uploadResult = await getUploadUrl({ fileName, contentType: file.type });
      const presignedUrl = uploadResult.data.uploadUrl;
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            const elapsed = (Date.now() - uploadStartTime.current) / 1000;
            const bytesPerSecond = event.loaded / elapsed;
            const remainingBytes = event.total - event.loaded;
            const secondsRemaining = bytesPerSecond > 0 ? remainingBytes / bytesPerSecond : 0;
            setUploadProgress(percent);
            setUploadDetails(prev => ({ ...prev, uploadedBytes: event.loaded, timeRemaining: secondsRemaining, status: 'uploading' }));
          }
        };
        xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`Upload failed with status ${xhr.status}`)); };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.onabort = () => reject(new Error('Upload aborted'));
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
      const fileUrl = uploadResult.data.fileUrl;
      let durationMs = 10000;
      if (isVideo) durationMs = await getVideoDuration(file);
      await addDoc(collection(db, 'assets'), {
        name: file.name, url: fileUrl, key: uploadResult.data.fileName, type: fileType, durationMs: durationMs, fileSize: file.size,
        thumbnailUrl: null, spriteUrl: null, processingStatus: isVideo ? 'processing' : 'ready',
        uploadedAt: Date.now(), equipmentTags: [], equipmentQuantities: {}, movementCategories: [], primaryBodyGroups: [], secondaryBodyGroups: [], bodyGroups: [], programmingNotes: '', substitutes: [],
        ...(viewMode === 'user' ? { userId: currentUser.uid } : {})
      });

      devLog(`Successfully uploaded: ${file.name}`);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(`Upload failed for ${file.name}: ${error.message}`);
    }
  };

  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'image/jpeg', 'image/png', 'image/gif'];
      const newFiles = Array.from(files).filter(file => {
        if (!validTypes.includes(file.type)) { alert(`Skipping ${file.name}: Invalid file type.`); return false; }
        return true;
      });
      if (showUploadModal && !uploading) setPendingFiles(prev => [...prev, ...newFiles]);
      else await handleFiles(files);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // User image upload — images only, routed through the shared upload pipeline.
  // Server (getUploadUrl) and Firestore rules enforce the image-only boundary;
  // this is the client-side guard so a user gets a clear message, not a server error.
  const handleUserImageSelect = async (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const imageFiles = Array.from(files).filter(file => {
        if (!imageTypes.includes(file.type)) {
          alert(`Skipping ${file.name}: only images (JPG, PNG, GIF, WebP) can be uploaded.`);
          return false;
        }
        return true;
      });
      if (imageFiles.length > 0) {
        if (showUploadModal && !uploading) setPendingFiles(prev => [...prev, ...imageFiles]);
        else await handleFiles(imageFiles);
      }
    }
    if (userFileInputRef.current) userFileInputRef.current.value = '';
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => { devLog('Video duration detection timed out'); resolve(270000); }, 5000);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        clearTimeout(timeout);
        window.URL.revokeObjectURL(video.src);
        const durationSeconds = Math.round(video.duration);
        resolve(durationSeconds * 1000);
      };
      video.onerror = () => { clearTimeout(timeout); resolve(270000); };
      video.src = URL.createObjectURL(file);
    });
  };

  const handleDeleteAsset = async (assetId) => {
    if (window.confirm('Are you sure you want to delete this asset? It will be removed from all playlists.')) {
      try {
        const playlistsSnapshot = await getDocs(collection(db, 'playlists'));
        for (const playlistDoc of playlistsSnapshot.docs) {
          const playlistData = playlistDoc.data();
          const assets = playlistData.assets || [];
          if (assets.includes(assetId)) {
            const updatedAssets = assets.filter(id => id !== assetId);
            await updateDoc(doc(db, 'playlists', playlistDoc.id), { assets: updatedAssets });
          }
        }
        await deleteDoc(doc(db, 'assets', assetId));
      } catch (error) {
        console.error('Error deleting asset:', error);
        alert('Error deleting asset: ' + error.message);
      }
    }
  };

  const handleRenameAsset = async (assetId, currentName) => {
    const newName = window.prompt('Enter new name:', currentName);
    if (newName && newName.trim() && newName !== currentName) {
      try {
        await updateDoc(doc(db, 'assets', assetId), { name: newName.trim() });
      } catch (error) {
        console.error('Error renaming asset:', error);
        alert('Error renaming asset: ' + error.message);
      }
    }
  };

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      alert('URL copied to clipboard!');
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('URL copied to clipboard!');
    }
  };

  const handleDuplicateAsset = async (asset) => {
    try {
      let newAsset;
      
      if (asset.type === 'layout') {
        // Duplicate layout with all zones and overlay
        newAsset = {
          name: asset.name + ' (Copy)',
          type: 'layout',
          layoutContentType: asset.layoutContentType,
          orientation: asset.orientation || 'landscape',
          overlayUrl: asset.overlayUrl || null,
          zones: (asset.zones || []).map(zone => ({ ...zone })), // Deep copy zones
          createdAt: Date.now(),
          uploadedAt: Date.now(),
          updatedAt: Date.now()
        };
            } else {
        // Duplicate video/image
        newAsset = {
          name: asset.name + ' (Copy)',
          url: asset.url,
          key: asset.key || null,
          type: asset.type,
          durationMs: asset.durationMs,
          fileSize: asset.fileSize,
          thumbnailUrl: asset.thumbnailUrl || null,
          spriteUrl: asset.spriteUrl || null,
          processingStatus: asset.processingStatus || 'ready',
          uploadedAt: Date.now(),
          equipmentTags: asset.equipmentTags || [],
          equipmentQuantities: asset.equipmentQuantities || {},
          movementCategories: asset.movementCategories || [],
          primaryBodyGroups: asset.primaryBodyGroups || [],
          secondaryBodyGroups: asset.secondaryBodyGroups || [],
          bodyGroups: asset.bodyGroups || [],
          programmingNotes: asset.programmingNotes || '',
          substitutes: asset.substitutes || []
        };
      }
      
      await addDoc(collection(db, 'assets'), newAsset);
      setToast({ type: 'success', message: asset.type === 'layout' ? 'Layout duplicated' : 'Asset duplicated' });
    } catch (error) {
      console.error('Error duplicating asset:', error);
      alert('Error duplicating asset: ' + error.message);
    }
  };

  const handleReplaceVideoClick = (asset) => {
    setReplaceVideoAsset(asset);
    setTimeout(() => {
      replaceFileInputRef.current?.click();
    }, 0);
  };

  const handleReplaceFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !replaceVideoAsset) {
      setReplaceVideoAsset(null);
      return;
    }
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      alert('Invalid file type. Please use MP4, WebM, or MOV.');
      setReplaceVideoAsset(null);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
      return;
    }
    setIsReplacing(true);
    setReplaceProgress(0);
    try {
      const existingUrl = replaceVideoAsset.url;
      const urlParts = existingUrl.split('/');
      const existingKey = urlParts[urlParts.length - 1];
      devLog(`Replacing video: ${replaceVideoAsset.name}`);
      devLog(`Existing S3 key: ${existingKey}`);
      const functions = getFunctions();
      const getUploadUrl = httpsCallable(functions, 'getUploadUrl');
      const uploadResult = await getUploadUrl({ fileName: existingKey, contentType: file.type });
      const presignedUrl = uploadResult.data.uploadUrl;
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setReplaceProgress(percent);
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.onabort = () => reject(new Error('Upload aborted'));
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
      const newDurationMs = await getVideoDuration(file);
      await updateDoc(doc(db, 'assets', replaceVideoAsset.id), {
        durationMs: newDurationMs,
        fileSize: file.size,
        thumbnailUrl: null,
        spriteUrl: null,
        processingStatus: 'processing',
        replacedAt: Date.now()
      });
      devLog(`Successfully replaced video: ${replaceVideoAsset.name}`);
      setToast({ type: 'success', message: 'Video replaced successfully' });
    } catch (error) {
      console.error('Error replacing video:', error);
      alert(`Failed to replace video: ${error.message}`);
    } finally {
      setIsReplacing(false);
      setReplaceProgress(0);
      setReplaceVideoAsset(null);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '0 sec';
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds} sec`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    // Handle Firestore Timestamp objects
    const ts = typeof timestamp === 'number' ? timestamp : timestamp.toMillis ? timestamp.toMillis() : timestamp.seconds ? timestamp.seconds * 1000 : timestamp;
    const now = Date.now();
    const diff = now - ts;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days} days ago`;
    return new Date(ts).toLocaleDateString();
};

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }, []);
  const handleDrop = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); const files = e.dataTransfer.files; if (files && files.length > 0) handleFiles(files); }, []);

  const getTagColor = (tag) => {
    if (tag.type === 'equipment') return { bg: '#e3f2fd', color: '#1976d2' };
    if (tag.type === 'movement') return { bg: '#f3e5f5', color: '#7b1fa2' };
    return { bg: '#e8f5e9', color: '#388e3c' };
  };

  if (loading) return <div className="loading">Loading assets...</div>;

  return (
    <div>
      {showMobileToast && (
        <div style={{ position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)', background: '#333', color: 'white', padding: '12px 24px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', zIndex: 10000, fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
          Open on desktop to edit layouts
        </div>
      )}

      {/* General Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: toast.type === 'success' ? '#4CAF50' : '#333',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10000,
          fontSize: '14px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {toast.type === 'success' && <RefreshCw size={16} />}
          {toast.message}
        </div>
      )}

      {/* Replace Video Progress Overlay */}
      {isReplacing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px 48px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <RefreshCw size={48} color="#2196F3" style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
              Replacing Video...
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              {replaceVideoAsset?.name}
            </div>
            <div style={{ width: '200px', height: '8px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${replaceProgress}%`,
                height: '100%',
                background: '#2196F3',
                transition: 'width 0.2s ease'
              }} />
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              {replaceProgress}%
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for Replace Video */}
      <input
        type="file"
        ref={replaceFileInputRef}
        onChange={handleReplaceFileSelect}
        accept="video/mp4,video/webm,video/quicktime"
        style={{ display: 'none' }}
      />

      <style>{`
        .assets-page-header {
          margin-bottom: 24px;
        }
        
        @media (max-width: 768px) {
          .page-title { display: none !important; }
          .assets-page-header > div:first-child { justify-content: center; }
          .filter-label, .btn-label { display: none !important; }
          .toolbar-divider { display: none !important; }
          .unified-toolbar { 
            flex-direction: column !important; 
            padding: 8px !important;
            gap: 8px !important;
          }
          .search-section { 
            width: 100% !important; 
            min-width: 100% !important;
          }
          .filter-pills { 
            width: 100% !important; 
            justify-content: center !important;
          }
          .action-buttons { display: none !important; }
          .view-mode-toggle { width: auto; }
        }
        @media (max-width: 480px) {
          .filter-pills button { padding: 6px 10px !important; font-size: 12px !important; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        
        .view-mode-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 8px;
          padding: 4px;
          gap: 4px;
        }
        .view-mode-toggle .toggle-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          background: transparent;
          color: #6b7280;
          transition: all 0.15s ease;
        }
        .view-mode-toggle .toggle-btn:hover:not(.active) {
          background: #e5e7eb;
          color: #374151;
        }
        .view-mode-toggle .toggle-btn.active {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .view-mode-toggle .toggle-btn.active.admin {
          background: #E63946;
          color: white;
        }
        .view-mode-toggle .toggle-btn.active.user {
          background: #10b981;
          color: white;
        }

        /* Library / My Files scope toggle - mirrors view-mode-toggle pattern */
        .scope-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 8px;
          padding: 4px;
          gap: 4px;
        }
        .scope-toggle .toggle-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          background: transparent;
          color: #6b7280;
          transition: all 0.15s ease;
        }
        .scope-toggle .toggle-btn:hover:not(.active) {
          background: #e5e7eb;
          color: #374151;
        }
        .scope-toggle .toggle-btn.active {
          background: white;
          color: #1a1a2e;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'white', borderRadius: '8px', width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <button onClick={cancelUpload} style={{ background: 'none', border: 'none', color: '#2196F3', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Cancel</button>
              <span style={{ fontWeight: 500, color: '#333' }}>{uploading ? `Uploading ${uploadDetails?.currentIndex || 1} of ${pendingFiles.length}` : `${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''} selected`}</span>
              {!uploading && <button onClick={addMoreFiles} style={{ background: 'none', border: 'none', color: '#2196F3', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>+ Add more</button>}
              {uploading && <div style={{ width: '70px' }} />}
            </div>
            <div style={{ padding: '24px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', maxHeight: '400px', overflowY: 'auto' }}>
              {pendingFiles.length > 1 && !uploading ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center', width: '100%' }}>
                  {pendingFiles.map((file, index) => (
                    <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '140px' }}>
                      <div style={{ position: 'relative', marginBottom: '8px' }}>
                        <div style={{ background: '#4CAF50', borderRadius: '8px', padding: '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FileVideo size={24} color="rgba(255,255,255,0.8)" />
                        </div>
                        <button onClick={() => removePendingFile(index)} style={{ position: 'absolute', top: '-8px', right: '-8px', width: '24px', height: '24px', borderRadius: '50%', background: '#666', color: 'white', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>×</button>
                      </div>
                      <div style={{ textAlign: 'center', color: '#333', width: '100%' }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div style={{ background: uploading ? '#1B5E20' : '#4CAF50', borderRadius: '8px', padding: '40px 60px', marginBottom: '16px' }}>
                    {uploading ? <div style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /> : <FileVideo size={32} color="rgba(255,255,255,0.8)" />}
                  </div>
                  <div style={{ textAlign: 'center', color: '#333' }}>
                    <div style={{ fontWeight: 500, fontSize: '16px', marginBottom: '4px' }}>{uploading ? uploadDetails?.fileName : pendingFiles[0]?.name}</div>
                    <div style={{ color: '#666', fontSize: '14px' }}>{formatFileSize(uploading ? uploadDetails?.fileSize : pendingFiles[0]?.size)}</div>
                  </div>
                </>
              )}
            </div>
            {uploading && uploadDetails && (
              <div style={{ padding: '16px 20px', borderTop: '1px solid #eee' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '20px', height: '20px', border: '2px solid #e0e0e0', borderTopColor: '#2196F3', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <div style={{ flex: 1, color: '#333' }}>
                    <div style={{ fontSize: '14px', fontWeight: 500 }}>Uploading: {uploadProgress}%</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{formatFileSize(uploadDetails.uploadedBytes)} of {formatFileSize(uploadDetails.fileSize)}</div>
                  </div>
                </div>
                <div style={{ marginTop: '12px', height: '4px', background: '#e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#2196F3', transition: 'width 0.2s ease' }} />
                </div>
              </div>
            )}
            {!uploading && (
              <div style={{ padding: '0 20px 20px' }}>
                <button onClick={startUpload} style={{ width: '100%', padding: '14px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', fontWeight: 500, cursor: 'pointer' }}>
                  Upload {pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="assets-page-header">
        {/* Row 1: Title + Toggle(s) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 className="page-title" style={{ fontSize: '20px', fontWeight: 500, color: '#333', margin: 0 }}>Files / Assets</h1>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Library / My Files Scope Toggle - visible only in User view */}
            {viewMode === 'user' && (
              <div className="scope-toggle">
                <button
                  className={`toggle-btn ${scope === 'library' ? 'active' : ''}`}
                  onClick={() => setScope('library')}
                  title="Shared library content"
                >
                  <LayoutGrid size={16} />
                  <span>Library</span>
                </button>
                <button
                  className={`toggle-btn ${scope === 'my-files' ? 'active' : ''}`}
                  onClick={() => setScope('my-files')}
                  title="Your own created content"
                >
                  <Folder size={16} />
                  <span>My Files</span>
                </button>
              </div>
            )}

            {/* Admin/User View Toggle */}
            {isAdmin && (
              <div className="view-mode-toggle">
                <button
                  className={`toggle-btn ${viewMode === 'admin' ? 'active admin' : ''}`}
                  onClick={() => setViewMode('admin')}
                  title="Admin View"
                >
                  <Shield size={16} />
                  <span>Admin</span>
                </button>
                <button
                  className={`toggle-btn ${viewMode === 'user' ? 'active user' : ''}`}
                  onClick={() => setViewMode('user')}
                  title="User View"
                >
                  <User size={16} />
                  <span>User</span>
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Row 2: Unified Bar */}
        <div className="unified-toolbar" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: 'white', 
          borderRadius: '12px', 
          padding: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          gap: '6px',
          flexWrap: 'wrap'
        }}>
          {/* Search Section */}
          <div ref={tagContainerRef} className="search-section" style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: '200px', position: 'relative' }}>
            {/* Search Mode Toggle */}
            <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px', marginRight: '8px', flexShrink: 0 }}>
              <button
                onClick={() => { setSearchMode('name'); setSelectedTags([]); setTagInputValue(''); }}
                style={{
                  padding: '8px 10px',
                  border: 'none',
                  borderRadius: '6px',
                  background: searchMode === 'name' ? '#E63946' : 'transparent',
                  color: searchMode === 'name' ? 'white' : '#666',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Search by name"
              >
                <Search size={16} />
              </button>
              <button
                onClick={() => { setSearchMode('tags'); setSearchQuery(''); }}
                style={{
                  padding: '8px 10px',
                  border: 'none',
                  borderRadius: '6px',
                  background: searchMode === 'tags' ? '#E63946' : 'transparent',
                  color: searchMode === 'tags' ? 'white' : '#666',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Filter by tags"
              >
                <Tag size={16} />
              </button>
            </div>
            
            {/* Name Search Input */}
            {searchMode === 'name' && (
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  placeholder="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                  style={{
                    width: '100%',
                    padding: '10px 32px 10px 12px',
                    border: 'none',
                    fontSize: '16px',
                    outline: 'none',
                    background: 'transparent'
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#999',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}
            
            {/* Tag Filter Input */}
            {searchMode === 'tags' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  gap: '6px',
                  overflow: 'hidden'
                }}
                onClick={() => tagInputRef.current?.focus()}
              >
                {selectedTags.map(tag => {
                  const colors = getTagColor(tag);
                  return (
                    <div
                      key={tag.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        backgroundColor: colors.bg,
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: colors.color,
                        flexShrink: 0
                      }}
                    >
                      <span>{tag.name}</span>
                      <X
                        size={14}
                        style={{ cursor: 'pointer', opacity: 0.7 }}
                        onClick={(e) => { e.stopPropagation(); removeTag(tag.id); }}
                      />
                    </div>
                  );
                })}
                
                <input
                  ref={tagInputRef}
                  type="text"
                  value={tagInputValue}
                  onChange={handleTagInputChange}
                  onKeyDown={handleTagKeyDown}
                  onFocus={() => setShowTagSuggestions(true)}
                  placeholder={selectedTags.length === 0 ? "Filter by tags..." : ""}
                  style={{
                    flex: 1,
                    minWidth: '80px',
                    border: 'none',
                    outline: 'none',
                    fontSize: '16px',
                    padding: '10px 0',
                    backgroundColor: 'transparent'
                  }}
                />
                
                {selectedTags.length > 0 && (
                  <X
                    size={18}
                    color="#999"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                    onClick={() => setSelectedTags([])}
                  />
                )}
              </div>
            )}
            
            {/* Suggestions Dropdown */}
            {searchMode === 'tags' && showTagSuggestions && filteredTagSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '8px',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 1000
              }}>
                {filteredTagSuggestions.some(t => t.type === 'equipment') && (
                  <>
                    <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>Equipment</div>
                    {filteredTagSuggestions.filter(t => t.type === 'equipment').map((tag) => {
                      const globalIdx = filteredTagSuggestions.indexOf(tag);
                      return (
                        <div key={tag.id} onClick={() => addTag(tag)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', cursor: 'pointer', backgroundColor: globalIdx === highlightedTagIndex ? '#f5f5f5' : 'white' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#2196F3' }} />
                          <span style={{ fontSize: '14px', color: '#333' }}>{tag.name}</span>
                          {tag.abbrev && <span style={{ fontSize: '12px', color: '#999' }}>({tag.abbrev})</span>}
                        </div>
                      );
                    })}
                  </>
                )}
                {filteredTagSuggestions.some(t => t.type === 'movement') && (
                  <>
                    <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>Movement</div>
                    {filteredTagSuggestions.filter(t => t.type === 'movement').map((tag) => {
                      const globalIdx = filteredTagSuggestions.indexOf(tag);
                      return (
                        <div key={tag.id} onClick={() => addTag(tag)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', cursor: 'pointer', backgroundColor: globalIdx === highlightedTagIndex ? '#f5f5f5' : 'white' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#9c27b0' }} />
                          <span style={{ fontSize: '14px', color: '#333' }}>{tag.name}</span>
                        </div>
                      );
                    })}
                  </>
                )}
                {filteredTagSuggestions.some(t => t.type === 'bodygroup') && (
                  <>
                    <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>Body Groups</div>
                    {filteredTagSuggestions.filter(t => t.type === 'bodygroup').map((tag) => {
                      const globalIdx = filteredTagSuggestions.indexOf(tag);
                      return (
                        <div key={tag.id} onClick={() => addTag(tag)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', cursor: 'pointer', backgroundColor: globalIdx === highlightedTagIndex ? '#f5f5f5' : 'white' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#4CAF50' }} />
                          <span style={{ fontSize: '14px', color: '#333' }}>{tag.name}</span>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Divider */}
          <div className="toolbar-divider" style={{ width: '1px', height: '28px', background: '#e5e7eb', flexShrink: 0 }} />
          
          {/* Filter Pills */}
          <div className="filter-pills" style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px', flexShrink: 0 }}>
            <button
              onClick={() => setTypeFilter('all')}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                background: typeFilter === 'all' ? 'white' : 'transparent',
                color: typeFilter === 'all' ? '#E63946' : '#666',
                boxShadow: typeFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              All
            </button>
            {(viewMode === 'admin' || (viewMode === 'user' && scope === 'my-files')) && (
              <button
                onClick={() => setTypeFilter('image')}
                style={{
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: typeFilter === 'image' ? 'white' : 'transparent',
                  color: typeFilter === 'image' ? '#E63946' : '#666',
                  boxShadow: typeFilter === 'image' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                <Image size={14} />
                <span className="filter-label">Images</span>
              </button>
            )}
            <button
              onClick={() => setTypeFilter('interval')}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: typeFilter === 'interval' ? 'white' : 'transparent',
                color: typeFilter === 'interval' ? '#E63946' : '#666',
                boxShadow: typeFilter === 'interval' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              <Film size={14} />
              <span className="filter-label">Intervals</span>
            </button>
            <button
              onClick={() => setTypeFilter('exercise')}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: typeFilter === 'exercise' ? 'white' : 'transparent',
                color: typeFilter === 'exercise' ? '#E63946' : '#666',
                boxShadow: typeFilter === 'exercise' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 6.5l11 11M6.5 17.5l11-11M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4"/></svg>
              <span className="filter-label">Exercises</span>
            </button>
            <button
              onClick={() => setTypeFilter('program')}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: typeFilter === 'program' ? 'white' : 'transparent',
                color: typeFilter === 'program' ? '#E63946' : '#666',
                boxShadow: typeFilter === 'program' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="4" rx="1"/><rect x="3" y="11" width="18" height="4" rx="1"/><rect x="3" y="18" width="18" height="3" rx="1"/></svg>
              <span className="filter-label">Programs</span>
            </button>
            <button
              onClick={() => setTypeFilter('layout')}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: typeFilter === 'layout' ? 'white' : 'transparent',
                color: typeFilter === 'layout' ? '#E63946' : '#666',
                boxShadow: typeFilter === 'layout' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              <LayoutGrid size={14} />
              <span className="filter-label">Layouts</span>
            </button>
          </div>
          
          {/* Divider - only show if admin buttons visible */}
          {!isMobile && viewMode === 'admin' && (
            <div className="toolbar-divider" style={{ width: '1px', height: '28px', background: '#e5e7eb', flexShrink: 0 }} />
          )}
          
          {/* Action Buttons - Admin only, desktop only */}
          {!isMobile && viewMode === 'admin' && (
            <div className="action-buttons" style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button 
                onClick={() => navigate('/layout/new')}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#f3f4f6',
                  color: '#333'
                }}
              >
                <LayoutGrid size={16} />
                <span className="btn-label">Layout</span>
              </button>
              <button 
                onClick={() => fileInputRef.current?.click()} 
                disabled={uploading}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#E63946',
                  color: 'white'
                }}
              >
                <Upload size={16} />
                <span className="btn-label">Upload</span>
              </button>
            </div>
          )}
          {/* User image upload - my-files scope, desktop only. Images only. */}
          {!isMobile && viewMode === 'user' && scope === 'my-files' && (
            <div className="action-buttons" style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button
                onClick={() => userFileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#E63946',
                  color: 'white'
                }}
              >
                <Upload size={16} />
                <span className="btn-label">Upload Image</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Multi-select delete bar - shows below unified bar when items selected */}
        {viewMode === 'admin' && selectedAssets.size > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              onClick={handleDeleteSelected} 
              disabled={isDeleting} 
              style={{ 
                background: '#d32f2f', 
                color: 'white', 
                padding: '8px 16px', 
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                opacity: isDeleting ? 0.6 : 1 
              }}
            >
              <Trash2 size={16} />
              {isDeleting ? 'Deleting...' : `Delete ${selectedAssets.size} selected`}
            </button>
          </div>
        )}
        
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="video/mp4,video/webm,video/quicktime,image/jpeg,image/png,image/gif" style={{ display: 'none' }} multiple />
        <input type="file" ref={userFileInputRef} onChange={handleUserImageSelect} accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} multiple />
      </div>

      {(searchQuery || selectedTags.length > 0 || typeFilter !== 'all' || viewMode === 'user') && (
        <div style={{ marginBottom: '16px', color: '#666' }}>
          Showing {filteredAssets.length} of {assets.length} assets
          {typeFilter !== 'all' && <> • <strong>{typeFilter}s</strong> only</>}
          {searchQuery && <> • matching "<strong>{searchQuery}</strong>"</>}
          {selectedTags.length > 0 && <> • filtered by <strong>{selectedTags.length} tag{selectedTags.length > 1 ? 's' : ''}</strong></>}
          {viewMode === 'user' && scope === 'library' && (typeFilter === 'all' || typeFilter === 'interval') && <> • <strong style={{ color: '#10b981' }}>filtered by your equipment</strong></>}
          {viewMode === 'user' && scope === 'my-files' && <> • <strong style={{ color: '#10b981' }}>your files only</strong></>}
        </div>
      )}

      <div className="card" onDragOver={viewMode === 'admin' ? handleDragOver : undefined} onDragLeave={viewMode === 'admin' ? handleDragLeave : undefined} onDrop={viewMode === 'admin' ? handleDrop : undefined} style={{ border: isDragOver && viewMode === 'admin' ? '2px dashed #2196F3' : '2px dashed transparent', background: isDragOver && viewMode === 'admin' ? 'rgba(33, 150, 243, 0.05)' : 'transparent', transition: 'all 0.2s ease' }}>
        <div className="assets-grid">
          {typeFilter === 'layout' && (
            <TemplatePickerTile onClick={() => setShowTemplatePicker(true)} />
          )}
          {filteredAssets.map(asset => (
            <AssetCard 
              key={asset.id} 
              asset={asset}
              signedUrlMap={signedUrlMap} 
              viewMode={viewMode}
              scope={scope}
              onDelete={handleDeleteAsset} 
              onRename={handleRenameAsset} 
              onCopyUrl={handleCopyUrl} 
              onEditTags={setEditTagsAsset} 
              onAddToPlaylist={setAddToPlaylistAsset} 
              onReplaceVideo={handleReplaceVideoClick} 
              onDuplicate={handleDuplicateAsset} 
              formatDuration={formatDuration} 
              formatFileSize={formatFileSize} 
              formatTimeAgo={formatTimeAgo} 
              onLayoutClick={handleLayoutClick} 
              isSelected={selectedAssets.has(asset.id)} 
              onToggleSelect={toggleAssetSelection} 
              onPreview={setPreviewAsset} 
              isMobile={isMobile} 
            />
          ))}
        </div>
        {filteredAssets.length === 0 && !searchQuery && selectedTags.length === 0 && typeFilter === 'all' && viewMode === 'admin' && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No assets yet. Click "Upload Files" to add videos and images.</div>}
        {filteredAssets.length === 0 && viewMode === 'user' && scope === 'library' && (typeFilter === 'all' || typeFilter === 'interval') && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No videos match your equipment profile.</div>}
        {viewMode === 'user' && scope === 'library' && typeFilter === 'exercise' && filteredAssets.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Exercise sequences you create appear in My Files.</div>}
        {viewMode === 'user' && scope === 'library' && typeFilter === 'program' && filteredAssets.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Programs you create appear in My Files.</div>}
        {viewMode === 'user' && scope === 'library' && typeFilter === 'layout' && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Layouts you create appear in My Files.</div>}
        {filteredAssets.length === 0 && viewMode === 'user' && scope === 'my-files' && typeFilter === 'layout' && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>You haven't created any layouts yet. Start from a preset with the Template picker above.</div>}
        {filteredAssets.length === 0 && viewMode === 'user' && scope === 'my-files' && typeFilter === 'image' && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>You haven't uploaded any images yet. Use "Upload Image" above.</div>}
        {filteredAssets.length === 0 && viewMode === 'user' && scope === 'my-files' && typeFilter === 'exercise' && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>You haven't created any exercise sequences yet.</div>}
        {filteredAssets.length === 0 && viewMode === 'user' && scope === 'my-files' && typeFilter === 'program' && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>You haven't created any programs yet.</div>}
        {filteredAssets.length === 0 && viewMode === 'user' && scope === 'my-files' && (typeFilter === 'all' || typeFilter === 'interval') && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>You haven't created any videos yet. Use the Video Builder to generate one.</div>}
        {filteredAssets.length === 0 && (searchQuery || selectedTags.length > 0 || typeFilter !== 'all') && viewMode === 'admin' && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
            <Tag size={48} color="#ddd" style={{ marginBottom: '16px' }} />
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>No assets match your filters</div>
            <div style={{ fontSize: '14px' }}>Try removing some filters to see more results</div>
          </div>
        )}
      </div>

    {previewAsset && (
  <VideoPreviewModal 
    asset={{
      ...previewAsset,
      signedUrl: signedUrlMap[previewAsset.key || previewAsset.url?.replace('https://pulse-signage.s3.us-east-2.amazonaws.com/', '')]
    }} 
    onClose={() => setPreviewAsset(null)} 
  />
)}
      {editTagsAsset && <EditTagsModal asset={editTagsAsset} onClose={() => setEditTagsAsset(null)} onSave={(updates) => devLog('Tags saved:', updates)} />}
      {addToPlaylistAsset && <AddToPlaylistModal asset={addToPlaylistAsset} onClose={() => setAddToPlaylistAsset(null)} isAdmin={isAdmin} currentUser={currentUser} />}
      {previewLayoutId && <LayoutPreviewModal layoutId={previewLayoutId} assets={assets} onClose={() => setPreviewLayoutId(null)} />}
      {showTemplatePicker && (
        <TemplatePickerModal
          currentUser={currentUser}
          isMobile={isMobile}
          onClose={() => setShowTemplatePicker(false)}
          onCreated={(name, errorMsg) => {
            if (errorMsg) {
              setToast({ type: 'error', message: errorMsg });
            } else {
              setToast({ type: 'success', message: `Layout "${name}" created` });
            }
          }}
        />
      )}
    </div>
  );
}

// AssetCard component with viewMode support
// Template Picker tile — pinned first in the Layouts view. Pure UI entry point (no asset doc).
function TemplatePickerTile({ onClick }) {
  return (
    <div className="asset-card" onClick={onClick} style={{ cursor: 'pointer', borderRadius: '0px', position: 'relative' }}>
      <div className="asset-thumbnail" style={{ position: 'relative', overflow: 'visible' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', background: 'linear-gradient(135deg, #ff9a3c 0%, #e8590c 100%)', flexDirection: 'column', gap: '8px', position: 'relative' }}>
          <span style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.92)', color: '#b8460a', fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '10px' }}>
            Template
          </span>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LayoutGrid size={48} color="white" />
            <div style={{ position: 'absolute', right: '-7px', bottom: '-7px', width: '22px', height: '22px', borderRadius: '50%', background: '#e8590c', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Plus size={13} color="white" strokeWidth={3} />
            </div>
          </div>
          <span style={{ fontSize: '12px', color: 'white', opacity: 0.9 }}>Start from a preset</span>
        </div>
      </div>
      <div className="asset-info">
        <div className="asset-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Template picker</div>
        <div className="asset-meta">Create a layout</div>
      </div>
    </div>
  );
}

// Schematic preview of a template — drawn from its real zones (position/size + contentType).
// Video zones (contentType 'playlist') show a play glyph; image zones ('asset') a photo glyph.
function TemplateSchematic({ zones }) {
  const list = Array.isArray(zones) ? zones : [];
  return (
    <div style={{ position: 'relative', width: '100%', height: '96px', background: '#f4f3ee', borderRadius: '8px', overflow: 'hidden' }}>
      {list.map((z, i) => {
        const isImage = z.contentType === 'asset';
        return (
          <div key={z.id || i} style={{
            position: 'absolute',
            top: `${z.top || 0}%`,
            left: `${z.left || 0}%`,
            width: `${z.width || 0}%`,
            height: `${z.height || 0}%`,
            background: isImage ? '#f6f0e6' : '#ffffff',
            border: '1px solid #cfcdc5',
            borderRadius: '3px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isImage
              ? <Image size={13} color="#8a8880" />
              : <Play size={13} color="#8a8880" />}
          </div>
        );
      })}
    </div>
  );
}

function TemplatePickerModal({ currentUser, isMobile, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'interval' | 'exercise'
  const [selectedId, setSelectedId] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [nameError, setNameError] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'assets'));
        const tmpl = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(a => a.type === 'layout' && a.isTemplate === true);
        setTemplates(tmpl);
      } catch (e) {
        console.error('Error loading templates:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const visible = templates.filter(t =>
    typeFilter === 'all' ? true : t.layoutContentType === typeFilter
  );

  const handleSelect = (id) => {
    setSelectedId(id);
    setNameInput('');
    setNameError(false);
  };

  const handleCreate = async (template) => {
    const name = nameInput.trim();
    if (!name) { setNameError(true); return; }
    setCreating(true);
    try {
      const newLayout = {
        name,
        type: 'layout',
        layoutContentType: template.layoutContentType,
        orientation: template.orientation || 'landscape',
        userId: currentUser.uid,
        source: 'template',
        zones: (template.zones || []).map(zone => ({ ...zone, contentId: null })),
        createdAt: Date.now(),
        uploadedAt: Date.now(),
        updatedAt: Date.now()
      };
      await addDoc(collection(db, 'assets'), newLayout);
      onCreated(name);
      onClose();
    } catch (e) {
      console.error('Error creating layout from template:', e);
      onCreated(null, 'Could not create layout');
    } finally {
      setCreating(false);
    }
  };

  const filterBtn = (val, label) => (
    <span
      onClick={() => setTypeFilter(val)}
      style={{
        fontSize: '13px', padding: '6px 14px', cursor: 'pointer',
        fontWeight: typeFilter === val ? 500 : 400,
        background: typeFilter === val ? '#f4f3ee' : 'transparent',
        color: typeFilter === val ? '#333' : '#777',
        borderLeft: val !== 'all' ? '1px solid #e5e3dc' : 'none'
      }}
    >{label}</span>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: '100%', maxWidth: '640px', margin: 'auto', overflow: 'hidden' }}>

        <div style={{ padding: '18px 22px 6px', textAlign: 'center', position: 'relative' }}>
          <div style={{ fontSize: '18px', fontWeight: 500, color: '#2c2c2a' }}>Choose a template</div>
          <div style={{ fontSize: '13px', color: '#888780', marginTop: '3px' }}>Pick a layout, name it, then fill each zone with your content</div>
          <div onClick={onClose} style={{ position: 'absolute', top: '16px', right: '18px', cursor: 'pointer' }}>
            <X size={20} color="#888" />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 22px 12px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'inline-flex', border: '1px solid #e5e3dc', borderRadius: '8px', overflow: 'hidden' }}>
            {filterBtn('all', 'All')}
            {filterBtn('interval', 'Interval')}
            {filterBtn('exercise', 'Exercise')}
          </div>
          <div style={{ display: 'flex', gap: '14px', fontSize: '12px', color: '#888780' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Play size={13} color="#888780" />Video</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Image size={13} color="#888780" />Image</span>
          </div>
        </div>

        <div style={{ padding: '0 22px 20px', maxHeight: '62vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>Loading templates…</div>
          ) : visible.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>No templates yet.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
              {visible.map(t => {
                const isSel = selectedId === t.id;
                const isExercise = t.layoutContentType === 'exercise';
                return (
                  <div
                    key={t.id}
                    onClick={() => !isSel && handleSelect(t.id)}
                    style={{
                      border: isSel ? '2px solid #E63946' : '1px solid #e5e3dc',
                      borderRadius: '12px', padding: '12px',
                      cursor: isSel ? 'default' : 'pointer'
                    }}
                  >
                    <TemplateSchematic zones={t.zones} />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px', gap: '8px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#2c2c2a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                      <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: 500, borderRadius: '10px', padding: '2px 8px', background: isExercise ? '#e1f5ee' : '#eeedfe', color: isExercise ? '#0f6e56' : '#4a3d7a' }}>
                        {isExercise ? 'Exercise' : 'Interval'}
                      </span>
                    </div>
                    {isSel ? (
                      <div style={{ marginTop: '10px' }}>
                        <input
                          type="text"
                          value={nameInput}
                          autoFocus
                          placeholder="Name this layout"
                          onChange={(e) => { setNameInput(e.target.value); if (nameError) setNameError(false); }}
                          style={{ width: '100%', boxSizing: 'border-box', marginBottom: nameError ? '4px' : '8px', padding: '8px 10px', border: `1px solid ${nameError ? '#E63946' : '#d0cdc5'}`, borderRadius: '8px', fontSize: isMobile ? '16px' : '13px' }}
                        />
                        {nameError && <div style={{ color: '#E63946', fontSize: '12px', marginBottom: '8px' }}>Enter a name first</div>}
                        <button
                          onClick={() => handleCreate(t)}
                          disabled={creating}
                          style={{ width: '100%', background: '#e8590c', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px', fontSize: '13px', fontWeight: 500, cursor: creating ? 'default' : 'pointer', opacity: creating ? 0.7 : 1 }}
                        >
                          {creating ? 'Creating…' : 'Create layout'}
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: '#b0aea6', marginTop: '8px' }}>Click to select</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function AssetCard({ asset,signedUrlMap, viewMode, scope, onDelete, onRename, onCopyUrl, onEditTags, onAddToPlaylist, onReplaceVideo, onDuplicate, formatDuration, formatFileSize, formatTimeAgo, onLayoutClick, isSelected, onToggleSelect, onPreview, isMobile }) {
  const [isHoveringCard, setIsHoveringCard] = useState(false);
  const [isHoveringCheckbox, setIsHoveringCheckbox] = useState(false);
  const [isHoveringThumbnail, setIsHoveringThumbnail] = useState(false);
  const [isHoveringPlayButton, setIsHoveringPlayButton] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [spriteFrame, setSpriteFrame] = useState(0);
  const scrubIntervalRef = useRef(null);
  const contextMenuRef = useRef(null);
  const isProcessing = asset.processingStatus === 'processing';
  const isLayout = asset.type === 'layout';
  const isVideo = asset.type === 'video';
  
  const isAdminMode = viewMode === 'admin';
  // A layout the user created from the template picker (source: 'template') — they may rename/delete it.
  // Provisioned layouts (received at signup) have no 'source' field and stay locked.
  const isUserTemplateLayout = isLayout && !isAdminMode && asset.source === 'template';
  // An image the user uploaded — in user mode, cards only ever render the user's own
  // assets (my-files scope filter), so !isAdminMode is sufficient proof of ownership.
  const isUserOwnImage = asset.type === 'image' && !isAdminMode;
  
  // Tag indicators - only show in admin mode
  const hasEquipmentTags = (asset.equipmentTags || []).length > 0;
  const hasMovementTags = (asset.movementCategories || []).length > 0;
  const hasBodyGroups = (asset.primaryBodyGroups || []).length > 0 || (asset.secondaryBodyGroups || []).length > 0 || (asset.bodyGroups || []).length > 0;
  const hasNotes = (asset.programmingNotes || '').trim().length > 0;
  const hasSubstitutes = (asset.substitutes || []).length > 0;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) setShowContextMenu(false);
    };
    if (showContextMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showContextMenu]);

  useEffect(() => {
    return () => { if (scrubIntervalRef.current) clearInterval(scrubIntervalRef.current); };
  }, []);

  const handleThumbnailMouseEnter = () => {
    setIsHoveringThumbnail(true);
    if (isProcessing || asset.type !== 'video') return;
    if (asset.spriteUrl) {
      setSpriteFrame(0);
      scrubIntervalRef.current = setInterval(() => setSpriteFrame(prev => (prev + 1) % 4), 600);
    }
  };

  const handleThumbnailMouseLeave = () => {
    setIsHoveringThumbnail(false);
    setSpriteFrame(0);
    if (scrubIntervalRef.current) { clearInterval(scrubIntervalRef.current); scrubIntervalRef.current = null; }
  };

  // Only show checkbox in admin mode
  const showCheckbox = isAdminMode && (isHoveringCard || isSelected);
  let checkboxBg = isSelected ? '#4CAF50' : isHoveringCheckbox ? 'white' : 'rgba(0,0,0,0.2)';
  let checkboxBorder = isSelected ? '2px solid #4CAF50' : isHoveringCheckbox ? '2px solid #d32f2f' : '2px solid rgba(150,150,150,0.9)';

  return (
    <div className="asset-card" onClick={() => isLayout && onLayoutClick && onLayoutClick(asset.id)} onMouseEnter={() => setIsHoveringCard(true)} onMouseLeave={() => { setIsHoveringCard(false); setShowContextMenu(false); }} style={{ cursor: isLayout ? 'pointer' : 'default', borderRadius: '0px', position: 'relative', ...(isLayout && asset.isTemplate ? { border: '2px solid #e8590c' } : {}), ...((!isMobile && isVideo && showContextMenu) ? { overflow: 'visible' } : {}) }}>
      {/* Checkbox - Admin mode only */}
      {isAdminMode && (
        <div onClick={(e) => onToggleSelect(asset.id, e)} onMouseEnter={() => setIsHoveringCheckbox(true)} onMouseLeave={() => setIsHoveringCheckbox(false)} style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10, cursor: 'pointer', pointerEvents: showCheckbox ? 'auto' : 'none', width: '24px', height: '24px', borderRadius: '50%', background: checkboxBg, border: checkboxBorder, display: isMobile ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', opacity: showCheckbox && !isMobile ? 1 : 0, transition: 'opacity 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
          {isSelected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>}
        </div>
      )}

      {/* Thumbnail */}
      <div className="asset-thumbnail" onMouseEnter={handleThumbnailMouseEnter} onMouseLeave={handleThumbnailMouseLeave} onClick={(e) => { if (isMobile && isVideo && !isProcessing) { e.preventDefault(); e.stopPropagation(); onPreview(asset); } }} style={{ position: 'relative', overflow: 'visible', cursor: (isMobile && isVideo && !isProcessing) ? 'pointer' : 'default' }}>
        {isLayout ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', background: asset.layoutContentType === 'exercise' ? 'linear-gradient(135deg, #1d9e75 0%, #0f6e56 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', flexDirection: 'column', gap: '8px', position: 'relative' }}>
            {asset.layoutContentType && (
              <span style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.92)', color: asset.layoutContentType === 'exercise' ? '#0f6e56' : '#4a3d7a', fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '10px' }}>
                {asset.layoutContentType === 'exercise' ? 'Exercise' : 'Interval'}
              </span>
            )}
            <LayoutGrid size={48} color="white" />
            <span style={{ fontSize: '12px', color: 'white', opacity: 0.9 }}>{asset.zones?.length || 0} zones</span>
            {asset.isTemplate && (
              <span style={{ position: 'absolute', bottom: '8px', left: '8px', background: '#e8590c', color: 'white', fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                Template
              </span>
            )}
            {/* Context menu button for layouts - Admin, or user's own template-created layout */}
            {(isAdminMode || isUserTemplateLayout) && (isMobile || isHoveringCard) && (
              <div onClick={(e) => { e.stopPropagation(); setShowContextMenu(!showContextMenu); }} style={{ position: 'absolute', bottom: '8px', right: '8px', width: '28px', height: '28px', borderRadius: '4px', background: showContextMenu ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 6 }}>
                <MoreVertical size={16} color="white" />
              </div>
            )}
          </div>
        ) : isVideo ? (
          <>
            {isProcessing ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888', background: '#f0f0f0', flexDirection: 'column', gap: '8px' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ animation: 'spin 2s linear infinite' }}><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                <span style={{ fontSize: '11px', color: '#666' }}>Processing...</span>
              </div>
            ) : (
              <>
                {asset.spriteUrl && isHoveringThumbnail ? (
                  <div style={{ width: '100%', height: '100%', backgroundImage: `url(${asset.spriteUrl})`, backgroundPosition: `${(spriteFrame / 3) * 100}% 0`, backgroundSize: '400% 100%', backgroundRepeat: 'no-repeat' }} />
                ) : asset.spriteUrl ? (
                  <div style={{ width: '100%', height: '100%', backgroundImage: `url(${asset.spriteUrl})`, backgroundPosition: '0% 0', backgroundSize: '400% 100%', backgroundRepeat: 'no-repeat' }} />
                ) : asset.thumbnailUrl ? (
                  <img src={asset.thumbnailUrl} alt={asset.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666', background: '#1a1a1a' }}><Film size={48} /></div>
                )}
                {/* Play button */}
                {!isMobile && isHoveringThumbnail && (
                  <div onClick={(e) => { e.stopPropagation(); onPreview(asset); }} onMouseEnter={() => setIsHoveringPlayButton(true)} onMouseLeave={() => setIsHoveringPlayButton(false)} style={{ position: 'absolute', top: '8px', right: '8px', width: '32px', height: '32px', borderRadius: '6px', background: isHoveringPlayButton ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 5 }}>
                    <Play size={18} color="white" style={{ marginLeft: '2px' }} />
                  </div>
                )}
                {/* Tag indicator dots - Admin mode only */}
                {isAdminMode && (hasEquipmentTags || hasMovementTags || hasBodyGroups || hasNotes || hasSubstitutes) && (
                  <div style={{ position: 'absolute', bottom: '8px', left: '8px', display: 'flex', gap: '4px', zIndex: 4 }}>
                    {hasEquipmentTags && (
                      <div title={`Equipment: ${(asset.equipmentTags || []).join(', ')}`} style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#2196F3', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'default' }} />
                    )}
                    {hasMovementTags && (
                      <div title={`Movement: ${(asset.movementCategories || []).join(', ')}`} style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#9c27b0', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'default' }} />
                    )}
                    {hasBodyGroups && (
                      <div title={`Body Groups: ${[...(asset.primaryBodyGroups || []), ...(asset.secondaryBodyGroups || [])].join(', ') || (asset.bodyGroups || []).join(', ')}`} style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#4CAF50', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'default' }} />
                    )}
                    {hasNotes && (
                      <div title={`Notes: ${(asset.programmingNotes || '').substring(0, 100)}${(asset.programmingNotes || '').length > 100 ? '...' : ''}`} style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ff9800', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'default' }} />
                    )}
                    {hasSubstitutes && (
                      <div title={`${(asset.substitutes || []).length} substitute(s)`} style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#e91e63', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'default' }} />
                    )}
                  </div>
                )}
                {/* Context menu button */}
                {(isMobile || isHoveringCard) && (
                  <div onClick={(e) => { e.stopPropagation(); setShowContextMenu(!showContextMenu); }} style={{ position: 'absolute', bottom: '8px', right: '8px', width: '28px', height: '28px', borderRadius: '4px', background: showContextMenu ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 6 }}>
                    <MoreVertical size={16} color="white" />
                  </div>
                )}
              </>
            )}
          </>
        ) : (
<div style={{ width: '100%', height: '100%' }}>
{(signedUrlMap?.[asset.key] || asset.url) ? (
  // Commit D: PREFER the freshly-signed URL; fall back to the stored url only while the
  // getSignedUrls round-trip is still in flight. (Old order put asset.url first, but it is
  // ALWAYS truthy, so the signed url never applied and an expired stored url 403'd -> the
  // broken-thumbnail bug. onError remains as a secondary net for the in-flight window.)
  <img
    src={signedUrlMap?.[asset.key] || asset.url}
    alt={asset.name}
    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
    onError={(e) => {
      const fresh = signedUrlMap?.[asset.key];
      if (fresh && e.currentTarget.dataset.fallback !== 'true') {
        e.currentTarget.dataset.fallback = 'true';
        e.currentTarget.src = fresh;
      }
    }}
  />
) : (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', background: '#f0f0f0' }}><Image size={32} /></div>
)}
{/* Context menu button (images) — admin, or user's own uploaded image */}
{(isAdminMode || isUserOwnImage) && (isMobile || isHoveringCard) && (
  <div onClick={(e) => { e.stopPropagation(); setShowContextMenu(!showContextMenu); }} style={{ position: 'absolute', bottom: '8px', right: '8px', width: '28px', height: '28px', borderRadius: '4px', background: showContextMenu ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 6 }}>
    <MoreVertical size={16} color="white" />
  </div>
)}
</div>
        )}
      </div>

      {/* Context menu dropdown */}
      {showContextMenu && (
        <ContextMenu 
          asset={asset}
          viewMode={viewMode}
          scope={scope}
          isMobile={isMobile}
          onEditTags={onEditTags}
          onAddToPlaylist={onAddToPlaylist}
          onReplaceVideo={onReplaceVideo}
          onDuplicate={onDuplicate}
          onRename={onRename}
          onCopyUrl={onCopyUrl}
          onDelete={onDelete}
          setShowContextMenu={setShowContextMenu}
          setIsHoveringCard={setIsHoveringCard}
          contextMenuRef={contextMenuRef}
        />
      )}

      {/* Info section */}
      <div className="asset-info">
        <div className="asset-name" title={asset.name} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{asset.name}</div>
        <div className="asset-meta">
          {isLayout ? <>Layout • {asset.zones?.length || 0} zones</> : <>{isVideo ? 'Video' : 'Image'} • {formatFileSize(asset.fileSize || 0)} • <strong>{formatDuration(asset.durationMs)}</strong></>}
          {asset.uploadedAt && <span style={{ display: 'block', marginTop: '2px', opacity: 0.7 }}>Added {formatTimeAgo(asset.uploadedAt)}</span>}
        </div>
        {/* Delete button - Admin mode only */}
        {isAdminMode && (
          <button className="btn btn-secondary" style={{ marginTop: '8px', padding: '4px 8px', fontSize: '12px' }} onClick={(e) => { e.stopPropagation(); onDelete(asset.id); }}><Trash2 size={14} />Delete</button>
        )}
      </div>
    </div>
  );
}

// Context Menu component with viewMode support
function ContextMenu({ asset, viewMode, scope, isMobile, onEditTags, onAddToPlaylist, onReplaceVideo, onDuplicate, onRename, onCopyUrl, onDelete, setShowContextMenu, setIsHoveringCard, contextMenuRef }) {
  const [hoveredItem, setHoveredItem] = useState(null);
  const isVideo = asset.type === 'video';
  const isLayout = asset.type === 'layout';
  const isImage = asset.type === 'image';
  const isAdminMode = viewMode === 'admin';
  const isUserTemplateLayout = isLayout && !isAdminMode && asset.source === 'template';
  const isUserOwnImage = isImage && !isAdminMode;
  const isProgram = asset.pageMode === 'program';
  const isUserOwnVideo = isVideo && !isAdminMode && scope === 'my-files';

  // User mode (desktop or mobile): Only "Add to Playlist" (not for layouts)
  // Admin mode on mobile: "Add to Playlist" only (existing behavior, not for layouts)
  // Admin mode on desktop: Full menu (different for layouts vs videos/images)
  
  let menuItems;
  
  if (isImage) {
    // Images - Admin, or a user's own uploaded image: Rename (Delete via showDelete below).
    // In user mode, image cards only render the user's own uploads (my-files scope).
    menuItems = [
      { id: 'rename', label: 'Rename', icon: <Edit3 size={16} color="#666" />, onClick: () => { setShowContextMenu(false); onRename(asset.id, asset.name); }, defaultBg: 'transparent', hoverBg: '#f5f5f5' },
    ];
  } else if (isLayout) {
    if (isUserTemplateLayout) {
      // User's own template-created layout: Rename (Delete added below via showDelete)
      menuItems = [
        { id: 'rename', label: 'Rename', icon: <Edit3 size={16} color="#666" />, onClick: () => { setShowContextMenu(false); onRename(asset.id, asset.name); }, defaultBg: 'transparent', hoverBg: '#f5f5f5' },
      ];
    } else if (isAdminMode && !isMobile) {
      menuItems = [
        { id: 'duplicate', label: 'Duplicate', icon: <Copy size={16} color="#9c27b0" />, onClick: () => { setShowContextMenu(false); onDuplicate(asset); }, defaultBg: '#f3e5f5', hoverBg: '#e1bee7' },
        { id: 'rename', label: 'Rename', icon: <Edit3 size={16} color="#666" />, onClick: () => { setShowContextMenu(false); onRename(asset.id, asset.name); }, defaultBg: 'transparent', hoverBg: '#f5f5f5' },
      ];
    } else {
      // Admin mobile - just show duplicate
      menuItems = [
        { id: 'duplicate', label: 'Duplicate', icon: <Copy size={16} color="#9c27b0" />, onClick: () => { setShowContextMenu(false); onDuplicate(asset); }, defaultBg: '#f3e5f5', hoverBg: '#e1bee7' },
      ];
    }
  } else if (!isAdminMode) {
    // User mode (desktop + mobile), videos.
    // Own My Files video: Rename + Add to Playlist (Delete via showDelete).
    // Shared library video: Add to Playlist only.
    if (isUserOwnVideo) {
      menuItems = [
        { id: 'rename', label: 'Rename', icon: <Edit3 size={16} color="#666" />, onClick: () => { setShowContextMenu(false); onRename(asset.id, asset.name); }, defaultBg: 'transparent', hoverBg: '#f5f5f5' },
        { id: 'playlist', label: 'Add to Playlist', icon: <ListPlus size={16} color="#4CAF50" />, onClick: () => { setShowContextMenu(false); onAddToPlaylist(asset); }, defaultBg: '#e8f5e9', hoverBg: '#c8e6c9' },
      ];
    } else {
      menuItems = [
        { id: 'playlist', label: 'Add to Playlist', icon: <ListPlus size={16} color="#4CAF50" />, onClick: () => { setShowContextMenu(false); onAddToPlaylist(asset); }, defaultBg: '#e8f5e9', hoverBg: '#c8e6c9' },
      ];
    }
  } else if (isMobile) {
    // Admin mode on mobile: Add to Playlist only (existing behavior)
    menuItems = [
      { id: 'playlist', label: 'Add to Playlist', icon: <ListPlus size={16} color="#4CAF50" />, onClick: () => { setShowContextMenu(false); onAddToPlaylist(asset); }, defaultBg: '#e8f5e9', hoverBg: '#c8e6c9' },
    ];
  } else {
    // Admin mode on desktop: Full menu
    menuItems = [
      { id: 'tags', label: 'Edit Tags', icon: <Tag size={16} color="#2196F3" />, onClick: () => { setShowContextMenu(false); onEditTags(asset); }, defaultBg: '#e3f2fd', hoverBg: '#bbdefb' },
      { id: 'playlist', label: 'Add to Playlist', icon: <ListPlus size={16} color="#4CAF50" />, onClick: () => { setShowContextMenu(false); onAddToPlaylist(asset); }, defaultBg: '#e8f5e9', hoverBg: '#c8e6c9' },
      ...(isVideo ? [{ id: 'replace', label: 'Replace Video', icon: <RefreshCw size={16} color="#ff9800" />, onClick: () => { setShowContextMenu(false); onReplaceVideo(asset); }, defaultBg: '#fff3e0', hoverBg: '#ffe0b2' }] : []),
      { id: 'duplicate', label: 'Duplicate', icon: <Copy size={16} color="#9c27b0" />, onClick: () => { setShowContextMenu(false); onDuplicate(asset); }, defaultBg: '#f3e5f5', hoverBg: '#e1bee7' },
      { id: 'rename', label: 'Rename', icon: <Edit3 size={16} color="#666" />, onClick: () => { setShowContextMenu(false); onRename(asset.id, asset.name); }, defaultBg: 'transparent', hoverBg: '#f5f5f5' },
      { id: 'copy', label: 'Copy URL', icon: <Link size={16} color="#666" />, onClick: () => { setShowContextMenu(false); onCopyUrl(asset.url); }, defaultBg: 'transparent', hoverBg: '#f5f5f5' },
    ];
  }

  // Program clips aren't interval/exercise content - never addable to a playlist.
  if (isProgram) menuItems = menuItems.filter(item => item.id !== 'playlist');

  // Show delete for admin, a user's own template-created layout/image, or a user's own video
  const showDelete = isAdminMode || isUserTemplateLayout || isUserOwnImage || isUserOwnVideo;

  return (
    <div 
      ref={contextMenuRef} 
      onMouseEnter={() => setIsHoveringCard(true)} 
      style={{ position: 'absolute', top: '40px', right: '8px', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', zIndex: 9999, minWidth: '160px', overflow: 'hidden' }} 
      onClick={(e) => e.stopPropagation()}
    >
      {menuItems.map((item, index) => (
        <button
          key={item.id}
          onClick={item.onClick}
          onMouseEnter={() => setHoveredItem(item.id)}
          onMouseLeave={() => setHoveredItem(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '10px 14px',
            background: hoveredItem === item.id ? item.hoverBg : item.defaultBg,
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#333',
            borderRadius: index === 0 ? '8px 8px 0 0' : (!showDelete && index === menuItems.length - 1) ? '0 0 8px 8px' : '0',
            transition: 'background 0.15s'
          }}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
      {/* Delete button - Admin mode only (both mobile and desktop) */}
      {showDelete && (
        <>
          <div style={{ height: '1px', background: '#eee', margin: '4px 0' }} />
          <button
            onClick={() => { setShowContextMenu(false); onDelete(asset.id); }}
            onMouseEnter={() => setHoveredItem('delete')}
            onMouseLeave={() => setHoveredItem(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: '10px 14px',
              background: hoveredItem === 'delete' ? '#ffebee' : 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#d32f2f',
              borderRadius: '0 0 8px 8px',
              transition: 'background 0.15s'
            }}
          >
            <Trash2 size={16} color="#d32f2f" />
            Delete
          </button>
        </>
      )}
    </div>
  );
}

// Add to Playlist Modal
function AddToPlaylistModal({ asset, onClose, isAdmin, currentUser }) {
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('now');

  const tomorrowGreen = '#43a047';
  const tomorrowGreenLight = '#e8f5e9';
  const tomorrowGreenBorder = '#a5d6a7';

  useEffect(() => {
    const fetchPlaylists = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'playlists'));
        const playlistData = snapshot.docs.map(doc => ({
  id: doc.id,
  ...doc.data()
})).filter(playlist => {
  // Owner filter (existing)
  const ownerOk = isAdmin ? (!playlist.userId || playlist.userId === currentUser?.uid) : (playlist.userId === currentUser?.uid);
  if (!ownerOk) return false;
  // Gate (doorway 2): playlist type must match this video's type.
  const assetType = asset.source === 'exercise-builder' ? 'exercise' : 'interval';
  return playlist.playlistContentType === assetType;
});
        playlistData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPlaylists(playlistData);
      } catch (error) {
        console.error('Error fetching playlists:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlaylists();
  }, [isAdmin, currentUser?.uid, asset.source]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleSave = async () => {
  if (!selectedPlaylist) return;
  
  setSaving(true);
  try {
    const playlistDoc = await getDocs(collection(db, 'playlists'));
    const playlist = playlistDoc.docs.find(d => d.id === selectedPlaylist);
    
    if (playlist) {
      if (activeTab === 'now') {
        const currentAssets = playlist.data().assets || [];
        
        if (currentAssets.includes(asset.id)) {
          alert('This asset is already in the selected playlist.');
          setSaving(false);
          return;
        }
        
        await updateDoc(doc(db, 'playlists', selectedPlaylist), {
          assets: [...currentAssets, asset.id]
        });
      } else {
        const pendingAssets = playlist.data().pendingAssets || [];
        
        if (pendingAssets.includes(asset.id)) {
          alert('This asset is already queued for tomorrow in this playlist.');
          setSaving(false);
          return;
        }
        
        // Auto-calculate swapAfter from playlist's swapTime
        const swapTime = playlist.data().swapTime || '00:00';
        const [swapHour, swapMinute] = swapTime.split(':').map(Number);
        const now = new Date();
        const swapDate = new Date();
        swapDate.setHours(swapHour, swapMinute, 0, 0);
        
        // If this time has already passed today, set it for tomorrow
        if (swapDate <= now) {
          swapDate.setDate(swapDate.getDate() + 1);
        }
        
        const swapAfter = swapDate.getTime();
        
        const updateData = {
  pendingAssets: [...pendingAssets, asset.id],
  swapAfter: swapAfter
};

if (!playlist.data().swapTime) {
  updateData.swapTime = swapTime;
}

await updateDoc(doc(db, 'playlists', selectedPlaylist), updateData);
      }
      
      onClose();
    }
  } catch (error) {
    console.error('Error adding to playlist:', error);
    alert('Error adding to playlist: ' + error.message);
  } finally {
    setSaving(false);
  }
};
  const isTomorrow = activeTab === 'tomorrow';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
      boxSizing: 'border-box'
    }} onClick={onClose}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '450px',
        maxWidth: '100%',
        maxHeight: '90vh',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 20px',
          textAlign: 'center',
          borderBottom: '1px solid #eee',
          flexShrink: 0
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: isTomorrow ? tomorrowGreen : 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            {isTomorrow ? <Calendar size={32} color="white" /> : <ListPlus size={32} color="white" />}
          </div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#333' }}>
            Add to Playlist
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#666' }}>
            {asset.name}
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #eee', flexShrink: 0 }}>
          <button
            onClick={() => { setActiveTab('now'); setSelectedPlaylist(''); }}
            style={{
              flex: 1,
              padding: '12px',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              border: 'none',
              cursor: 'pointer',
              color: activeTab === 'now' ? '#333' : '#888',
              borderBottom: activeTab === 'now' ? '2px solid #333' : '2px solid transparent',
              background: activeTab === 'now' ? '#f5f5f5' : 'transparent'
            }}
          >
            <ListPlus size={18} />
            Now
          </button>
          <button
            onClick={() => { setActiveTab('tomorrow'); setSelectedPlaylist(''); }}
            style={{
              flex: 1,
              padding: '12px',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              border: 'none',
              cursor: 'pointer',
              color: isTomorrow ? tomorrowGreen : '#888',
              borderBottom: isTomorrow ? `2px solid ${tomorrowGreen}` : '2px solid transparent',
              background: isTomorrow ? tomorrowGreenLight : 'transparent'
            }}
          >
            <Calendar size={18} />
            Tomorrow
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', paddingBottom: '16px', flex: 1, overflowY: 'auto' }}>
          {isTomorrow && (
            <div style={{
              fontSize: '12px',
              marginBottom: '16px',
              padding: '12px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: tomorrowGreen,
              background: tomorrowGreenLight,
              border: `1px dashed ${tomorrowGreenBorder}`
            }}>
              <Calendar size={14} />
              This video will replace the playlist content at 12:00 AM
            </div>
          )}

          <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#555' }}>
            Select Playlist
          </div>
          
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
              Loading playlists...
            </div>
          ) : playlists.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
              No playlists found. Create a playlist first.
            </div>
          ) : (
            <select
              value={selectedPlaylist}
              onChange={(e) => setSelectedPlaylist(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${isTomorrow && selectedPlaylist ? tomorrowGreen : '#ddd'}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white',
                cursor: 'pointer',
                color: '#333'
              }}
            >
              <option value="" style={{ color: '#999' }}>-- Select a Playlist --</option>
              {playlists.map((playlist) => {
                const itemCount = isTomorrow 
                  ? (playlist.pendingAssets || []).length 
                  : (playlist.assets || []).length;
                return (
                  <option key={playlist.id} value={playlist.id} style={{ color: '#333' }}>
                    {playlist.name} ({itemCount} {isTomorrow ? 'queued' : 'items'})
                  </option>
                );
              })}
            </select>
          )}
          {!loading && playlists.length === 0 && (
            <div style={{ fontSize: '13px', color: '#c62828', marginTop: '8px' }}>
              No {asset.source === 'exercise-builder' ? 'exercise' : 'interval'} playlists to add this to. Create one first.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '16px 24px',
          borderTop: '1px solid #eee',
          background: '#fafafa',
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              background: 'white',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              color: '#666'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedPlaylist || saving}
            style={{
              padding: '10px 24px',
              borderRadius: '6px',
              border: 'none',
              background: !selectedPlaylist || saving 
                ? '#ccc' 
                : isTomorrow ? tomorrowGreen : '#4CAF50',
              color: 'white',
              fontSize: '14px',
              fontWeight: 500,
              cursor: !selectedPlaylist || saving ? 'default' : 'pointer'
            }}
          >
            {saving ? 'Adding...' : isTomorrow ? 'Add to Tomorrow' : 'Add to Playlist'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Layout Preview Modal (User mode - allows content assignment)
function LayoutPreviewModal({ layoutId, assets, onClose }) {
  const [layout, setLayout] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [signedUrlMap, setSignedUrlMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);
  const { currentUser, isAdmin } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get layout from assets
        const layoutData = assets.find(a => a.id === layoutId);
        if (layoutData) {
          setLayout({ ...layoutData });
        }

        // Fetch playlists (all playlists for name resolution in zones)
        const playlistsSnapshot = await getDocs(collection(db, 'playlists'));
        const playlistsData = playlistsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPlaylists(playlistsData);

        // Fetch assets (non-layout) for asset-type zones
        const assetsSnapshot = await getDocs(collection(db, 'assets'));
        const assetsData = assetsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(a => a.type !== 'layout');
        setAllAssets(assetsData);
      } catch (error) {
        console.error('Error fetching layout data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [layoutId, assets, currentUser?.uid]);

  // Fetch signed CloudFront URLs for image assets so picker thumbnails render
  // (mirrors the Assets page — images live behind signed URLs, not asset.url directly).
  useEffect(() => {
    const fetchSignedUrls = async () => {
      const imageAssets = allAssets.filter(a => a.type === 'image');
      if (imageAssets.length === 0) return;

      const keys = imageAssets.map(a =>
        a.key || a.url?.replace('https://pulse-signage.s3.us-east-2.amazonaws.com/', '')
      ).filter(Boolean);

      if (keys.length === 0) return;

      try {
        const functions = getFunctions();
        const getSignedUrls = httpsCallable(functions, 'getSignedUrls');

        const chunks = [];
        for (let i = 0; i < keys.length; i += 50) {
          chunks.push(keys.slice(i, i + 50));
        }

        const allSignedUrls = {};
        for (const chunk of chunks) {
          const result = await getSignedUrls({ keys: chunk });
          if (result.data?.signedUrls) {
            Object.assign(allSignedUrls, result.data.signedUrls);
          }
        }

        setSignedUrlMap(allSignedUrls);
      } catch (error) {
        console.error('Failed to fetch signed URLs:', error);
      }
    };

    fetchSignedUrls();
  }, [allAssets]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const getContentName = (zone) => {
    if (!zone.contentId) return 'Not assigned';
    
    if (zone.contentType === 'playlist') {
      const playlist = playlists.find(p => p.id === zone.contentId);
      return playlist ? playlist.name : 'Unknown playlist';
    } else {
      const asset = allAssets.find(a => a.id === zone.contentId);
      return asset ? asset.name : 'Unknown asset';
    }
  };

  const handleZoneClick = (index) => {
    if (selectedZoneIndex === index) {
      setSelectedZoneIndex(null);
    } else {
      setSelectedZoneIndex(index);
      setSearchQuery('');
    }
  };

  const handleSelectContent = async (contentId) => {
    if (selectedZoneIndex === null || !layout) return;
    
    setSaving(true);
    try {
      const updatedZones = layout.zones.map((zone, index) => ({
        id: zone.id,
        name: zone.name,
        top: parseFloat(zone.top) || 0,
        left: parseFloat(zone.left) || 0,
        width: parseFloat(zone.width) || 50,
        height: parseFloat(zone.height) || 50,
        contentType: zone.contentType,
        contentId: index === selectedZoneIndex ? contentId : zone.contentId,
        color: zone.color,
        fitMode: zone.fitMode || 'fit',
        zIndex: zone.zIndex || 0
      }));

      console.log('zones being written:', JSON.stringify(updatedZones, null, 2));
      await updateDoc(doc(db, 'assets', layoutId), {
        zones: updatedZones,
        updatedAt: Date.now()
      });

      setLayout(prev => ({ ...prev, zones: updatedZones }));
      setSelectedZoneIndex(null);
      setToast({ message: 'Content updated successfully', type: 'success' });
    } catch (error) {
      console.error('Error updating zone content:', error);
      setToast({ message: 'Failed to update content', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const selectedZone = selectedZoneIndex !== null ? layout?.zones?.[selectedZoneIndex] : null;

  // Filter content based on zone type and search
  const filteredContent = selectedZone?.contentType === 'playlist'
    ? playlists.filter(p => {
        const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesOwner = p.userId === currentUser?.uid || !p.userId;
        // Gate (doorway 3, user path): only playlists whose type matches this layout's content type.
        const matchesType = !!layout?.layoutContentType && p.playlistContentType === layout.layoutContentType;
        return matchesSearch && matchesOwner && matchesType;
      })
    : allAssets.filter(a => {
        const matchesSearch = a.name?.toLowerCase().includes(searchQuery.toLowerCase());
        // Asset zones accept images only (mirrors LayoutEditor's asset picker).
        const matchesType = a.type === 'image';
        // Owner gate (mirrors the playlist branch): user's own images + shared library only.
        const matchesOwner = isAdmin ? (a.userId === currentUser?.uid || !a.userId) : (a.userId === currentUser?.uid);
        return matchesSearch && matchesType && matchesOwner;
      });

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{ color: 'white', fontSize: '16px' }}>Loading...</div>
      </div>
    );
  }

  if (!layout) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }} onClick={onClose}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <p>Layout not found</p>
          <button onClick={onClose} style={{
            padding: '10px 24px',
            background: '#E63946',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            marginTop: '12px'
          }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const zones = layout.zones || [];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
      boxSizing: 'border-box'
    }} onClick={onClose}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '800px',
        width: '95%',
        maxHeight: '90vh',
        overflow: 'auto'
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#1a1a2e', fontSize: '20px' }}>{layout.name}</h2>
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>
              {layout.orientation || 'Landscape'} • {zones.length} zone{zones.length !== 1 ? 's' : ''} {layout.overlayUrl && '• Has overlay'}
            </p>
            <p style={{ margin: '4px 0 0', color: '#999', fontSize: '12px' }}>
              Click a zone to change its content
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#999',
              padding: '4px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Layout Preview */}
        <div style={{
          position: 'relative',
          width: '100%',
          paddingBottom: layout.orientation === 'portrait' ? '177.78%' : '56.25%',
          background: '#e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #ddd'
        }}>
          {/* Render zones sorted by zIndex */}
          {[...zones]
            .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
            .map((zone, sortedIndex) => {
              // Find the real index in the zones array
              const realIndex = zones.findIndex(z => z.id === zone.id);
              const isSelected = selectedZoneIndex === realIndex;
              const contentName = getContentName(zone);
              const isUnassigned = !zone.contentId;

              return (
                <div
                  key={zone.id || sortedIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZoneClick(realIndex);
                  }}
                  style={{
                    position: 'absolute',
                    top: `${zone.top}%`,
                    left: `${zone.left}%`,
                    width: `${zone.width}%`,
                    height: `${zone.height}%`,
                    backgroundColor: zone.color || '#F4A460',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: '2px solid white',
                    borderBottom: '2px solid white',
                    boxSizing: 'border-box',
                    zIndex: zone.zIndex || 0,
                    cursor: 'pointer',
                    outline: isSelected ? '3px solid #E63946' : 'none',
                    outlineOffset: '-3px',
                    transition: 'outline 0.15s ease'
                  }}
                >
                  {/* Zone name */}
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(0,0,0,0.7)', textAlign: 'center', padding: '4px' }}>
                    {zone.name}
                  </span>
                  {/* Content name */}
                  <span style={{ 
                    fontSize: '12px', 
                    color: isUnassigned ? 'rgba(200,0,0,0.7)' : 'rgba(0,0,0,0.5)', 
                    textAlign: 'center', 
                    padding: '0 4px',
                    fontWeight: isUnassigned ? 600 : 400
                  }}>
                    {contentName}
                  </span>
                  {/* Change indicator */}
                  <span style={{ 
                    fontSize: '10px', 
                    color: 'rgba(0,0,0,0.4)', 
                    marginTop: '4px',
                    background: 'rgba(255,255,255,0.6)',
                    padding: '2px 8px',
                    borderRadius: '10px'
                  }}>
                    {isSelected ? 'Select below ▼' : 'Click to change'}
                  </span>
                  {/* Image-zone size hint — corner tag, exact px from the zone's dimensions on the 1280x720 canvas */}
                  {zone.contentType === 'asset' && (
                    <span style={{
                      position: 'absolute',
                      top: '6px',
                      right: '8px',
                      fontSize: '9px',
                      fontWeight: 500,
                      color: '#5a5a5a',
                      background: 'rgba(255,255,255,0.92)',
                      padding: '2px 7px',
                      borderRadius: '10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      whiteSpace: 'nowrap',
                      zIndex: 1
                    }}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.3 15.3 8.7 2.7a1 1 0 0 0-1.4 0l-4.6 4.6a1 1 0 0 0 0 1.4l12.6 12.6a1 1 0 0 0 1.4 0l4.6-4.6a1 1 0 0 0 0-1.4Z"/><path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/></svg>
                      {isUnassigned ? 'Add image · ' : ''}{Math.round(zone.width / 100 * 1280)} × {Math.round(zone.height / 100 * 720)} px
                    </span>
                  )}
                </div>
              );
            })}

          {/* Overlay Preview */}
          {layout.overlayUrl && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 1000
              }}
            >
              <img
                src={layout.overlayUrl}
                alt="Overlay"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill'
                }}
              />
            </div>
          )}

          {zones.length === 0 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#999',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '16px' }}>No zones defined</div>
            </div>
          )}
        </div>

        {/* Content Picker - shows when a zone is selected */}
        {selectedZone && (
          <div style={{
            marginTop: '16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {/* Picker Header */}
            <div style={{
              padding: '12px 16px',
              background: '#f5f5f5',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
                  {selectedZone.name}
                </span>
                <span style={{ fontSize: '13px', color: '#666', marginLeft: '8px' }}>
                  — Select {selectedZone.contentType === 'playlist' ? 'a playlist' : 'an asset'}
                </span>
              </div>
              <button
                onClick={() => setSelectedZoneIndex(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#999',
                  padding: '0 4px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Search */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Content Grid */}
            <div style={{ 
              padding: '12px 16px', 
              maxHeight: '250px', 
              overflowY: 'auto'
            }}>
              {selectedZone.contentType === 'playlist' ? (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
                  gap: '12px' 
                }}>
                  {filteredContent.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
                      No playlists found
                    </div>
                  ) : (
                    filteredContent.map(playlist => {
                      const isCurrentContent = selectedZone.contentId === playlist.id;
                      return (
                        <div
                          key={playlist.id}
                          onClick={() => !saving && handleSelectContent(playlist.id)}
                          style={{
                            padding: '12px',
                            border: isCurrentContent ? '2px solid #4CAF50' : '2px solid #eee',
                            borderRadius: '8px',
                            cursor: saving ? 'default' : 'pointer',
                            transition: 'all 0.15s ease',
                            background: isCurrentContent ? '#f0fff0' : 'white'
                          }}
                          onMouseEnter={(e) => { if (!isCurrentContent) e.currentTarget.style.borderColor = '#E63946'; }}
                          onMouseLeave={(e) => { if (!isCurrentContent) e.currentTarget.style.borderColor = '#eee'; }}
                        >
                          <div style={{ 
                            width: '100%', 
                            height: '60px', 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '8px'
                          }}>
                            <Film size={24} color="white" />
                          </div>
                          <div style={{ fontWeight: 500, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {playlist.name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                            {playlist.assets?.length || 0} items
                          </div>
                          {isCurrentContent && (
                            <div style={{ fontSize: '11px', color: '#4CAF50', marginTop: '4px', fontWeight: 600 }}>
                              ✓ Currently assigned
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                  gap: '12px' 
                }}>
                  {filteredContent.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
                      No assets found
                    </div>
                  ) : (
                    filteredContent.map(asset => {
                      const isCurrentContent = selectedZone.contentId === asset.id;
                      return (
                        <div
                          key={asset.id}
                          onClick={() => !saving && handleSelectContent(asset.id)}
                          style={{
                            border: isCurrentContent ? '2px solid #4CAF50' : '2px solid #eee',
                            borderRadius: '8px',
                            cursor: saving ? 'default' : 'pointer',
                            overflow: 'hidden',
                            transition: 'all 0.15s ease',
                            background: isCurrentContent ? '#f0fff0' : 'white'
                          }}
                          onMouseEnter={(e) => { if (!isCurrentContent) e.currentTarget.style.borderColor = '#E63946'; }}
                          onMouseLeave={(e) => { if (!isCurrentContent) e.currentTarget.style.borderColor = '#eee'; }}
                        >
                          <div style={{ 
                            width: '100%', 
                            height: '80px', 
                            background: '#1a1a1a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {asset.type === 'image' && (signedUrlMap[asset.key || asset.url?.replace('https://pulse-signage.s3.us-east-2.amazonaws.com/', '')] || asset.url) ? (
                              // Commit D: prefer the freshly-signed URL; fall back to stored url only while signing is in flight.
                              <img
                                src={signedUrlMap[asset.key || asset.url?.replace('https://pulse-signage.s3.us-east-2.amazonaws.com/', '')] || asset.url}
                                alt={asset.name}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  const k = asset.key || asset.url?.replace('https://pulse-signage.s3.us-east-2.amazonaws.com/', '');
                                  const fresh = signedUrlMap[k];
                                  if (fresh && e.currentTarget.dataset.fallback !== 'true') {
                                    e.currentTarget.dataset.fallback = 'true';
                                    e.currentTarget.src = fresh;
                                  }
                                }}
                              />
                            ) : asset.spriteUrl ? (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                backgroundImage: `url(${asset.spriteUrl})`,
                                backgroundPosition: '0% 0',
                                backgroundSize: '400% 100%',
                                backgroundRepeat: 'no-repeat'
                              }} />
                            ) : (
                              <Image size={24} color="#999" />
                            )}
                          </div>
                          <div style={{ padding: '8px' }}>
                            <div style={{ fontWeight: 500, fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {asset.name}
                            </div>
                            {isCurrentContent && (
                              <div style={{ fontSize: '10px', color: '#4CAF50', marginTop: '2px', fontWeight: 600 }}>
                                ✓ Assigned
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            borderRadius: '8px',
            background: toast.type === 'success' ? '#4CAF50' : '#E63946',
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            zIndex: 10001,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}>
            {toast.message}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: '20px', textAlign: 'right' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: '#E63946',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default Assets;